import { isAvailableApplePay } from '~/lib/isAvailableApplePay';
import { ApplePay } from '~/public/assets/payment/Apple_Pay_logo';
import { Alipay } from '~/public/assets/payment/alipay';
import { GooglePay } from '~/public/assets/payment/googlePayLogo';
import { Payment } from '~/public/assets/payment/payment';
import { Paypay } from '~/public/assets/payment/paypay';
import { Ticket } from '~/public/assets/payment/ticket';
import { Union } from '~/public/assets/payment/union';
import type {
  CarriersPaymentMethodVariables,
  PaymentMethodVariables,
} from '~/types/payment';
import type { GachaDetail } from '~/types/resources/gachaDetail';
import { Credit } from '../public/assets/payment/credit';

export const methods = [
  'credit',
  'paypay',
  'carriers:au',
  'carriers:docomo',
  'carriers:softbank',
  'unionpay',
  'alipay+',
  'play_ticket',
  'applepay',
  'googlepay',
] as const;

export const carriersMethods = [
  'carriers:au',
  'carriers:docomo',
  'carriers:softbank',
] as const;

export const PAYMENT_METHOD_KV: Record<string, (typeof methods)[number]> = {
  credit: 'credit',
  paypay: 'paypay',
  carriersAu: 'carriers:au',
  carriersDocomo: 'carriers:docomo',
  carriersSoftbank: 'carriers:softbank',
  unionpay: 'unionpay',
  alipayPlus: 'alipay+',
  playTicket: 'play_ticket',
  applepay: 'applepay',
  googlepay: 'googlepay',
};

export type PaymentMethod = {
  id: PaymentMethodVariables;
  payment_id: number;
  display_name: string;
  history_display_name: string;
  disable: boolean;
  icon?: React.ElementType;
  icon_image?: string;
};

export const payment_methods: PaymentMethod[] = [
  {
    id: 'credit',
    payment_id: 1,
    display_name: 'クレジットカード',
    history_display_name: 'クレジットカード',
    disable: false,
    icon: Credit,
  },
  {
    id: 'paypay',
    payment_id: 2,
    display_name: 'PayPay',
    history_display_name: 'PayPay',
    disable: false,
    icon: Paypay,
  },
  {
    id: 'carriers:au',
    payment_id: 3,
    display_name: 'キャリア決済',
    history_display_name: 'キャリア決済 - auかんたん決済',
    disable: false,
    icon: Payment,
  },
  {
    id: 'carriers:docomo',
    payment_id: 3,
    display_name: 'キャリア決済',
    history_display_name: 'キャリア決済 - d払い',
    disable: false,
    icon: Payment,
  },
  {
    id: 'carriers:softbank',
    payment_id: 3,
    display_name: 'キャリア決済',
    history_display_name:
      'キャリア決済 - ソフトバンクまとめて支払い・ワイモバイルまとめて支払い',
    disable: false,
    icon: Payment,
  },
  {
    id: 'unionpay',
    payment_id: 5,
    display_name: 'UnionPay(銀聯)',
    history_display_name: 'UnionPay(銀聯)',
    disable: false,
    icon: Union,
  },
  {
    id: 'alipay+',
    payment_id: 6,
    display_name: 'AliPay+',
    history_display_name: 'AliPay+',
    disable: false,
    icon: Alipay,
  },
  {
    id: 'play_ticket',
    payment_id: 7,
    display_name: '無料チケット',
    history_display_name: '無料チケット',
    disable: false,
    icon: Ticket,
  },
  {
    id: 'applepay',
    payment_id: 8,
    display_name: 'Apple Pay',
    history_display_name: 'Apple Pay',
    disable: !isAvailableApplePay,
    icon: ApplePay,
  },
  {
    id: 'googlepay',
    payment_id: 9,
    display_name: 'Google Pay',
    history_display_name: 'Google Pay',
    disable: false,
    icon: GooglePay,
  },
];

export const isMethods = (str: string): str is PaymentMethodVariables => {
  return methods.some((method: string) => str === method);
};

export const findPaymentIDByID = (id: PaymentMethodVariables): number => {
  const method: PaymentMethod | undefined = payment_methods.find(
    (method: PaymentMethod) => {
      return method.id === id;
    }
  );
  if (method === undefined) throw new Error();
  return method.payment_id;
};

export const findPaymentByID = (id: string): PaymentMethod | null => {
  const method: PaymentMethod | undefined = payment_methods.find(
    (method: PaymentMethod) => {
      return method.id === id;
    }
  );
  if (method === undefined) return null;
  return method;
};

export const carriers_payment_methods: PaymentMethod[] = [
  {
    id: 'carriers:au',
    payment_id: 1,
    display_name: 'au かんたん決済',
    history_display_name: 'au かんたん決済',
    disable: false,
    icon: undefined,
    icon_image: '/assets/payment/au.png',
  },
  {
    id: 'carriers:docomo',
    payment_id: 2,
    display_name: 'd払い',
    history_display_name: 'd払い',
    disable: false,
    icon: undefined,
    icon_image: '/assets/payment/docomo.png',
  },
  {
    id: 'carriers:softbank',
    payment_id: 3,
    display_name: 'ソフトバンクまとめて支払い・ワイモバイルまとめて支払い',
    history_display_name:
      'ソフトバンクまとめて支払い・ワイモバイルまとめて支払い',
    disable: false,
    icon: undefined,
    icon_image: '/assets/payment/softbank.png',
  },
];

export const isCarriersMethods = (
  str: string
): str is Extract<
  PaymentMethodVariables,
  'carriers:au' | 'carriers:docomo' | 'carriers:softbank' | 'unionpay'
> => {
  return carriersMethods.some((method: string) => str === method);
};

export const findCarriersPaymentIDByID = (
  id: CarriersPaymentMethodVariables
): number => {
  const method: PaymentMethod | undefined = carriers_payment_methods.find(
    (method: PaymentMethod) => {
      return method.id === id;
    }
  );
  if (method === undefined) throw new Error();
  return method.payment_id;
};

export const generateReturnUrl = (
  gacha: GachaDetail,
  order_id: string
): string => {
  return `${location.origin}/e/${gacha.unique_name}/orders/${order_id}/redirect`;
};
